import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  getApplications, getApplication, approveApplication, rejectApplication, requestChangesApplication,
  suspendApplication, reinstateApplication, updatePayoutStatus,
  deleteApplications, deleteAllApplications,
} from '../api/admin';
import { useAuth } from '../context/AuthContext';
import Badge from '../components/Badge';
import Pagination from '../components/Pagination';
import styles from './Applications.module.css';

function fmt(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

function isImageUrl(url) {
  if (!url) return false;
  // Strip query params (Supabase signed URLs have ?token=...) before checking ext
  const clean = url.split('?')[0];
  return /\.(jpg|jpeg|png|webp|gif)$/i.test(clean);
}

function isPdfUrl(url) {
  if (!url) return false;
  const clean = url.split('?')[0];
  return /\.pdf($|\?)/i.test(clean);
}

// ── Lightbox ──────────────────────────────────────────────────────────────────
function Lightbox({ url, onClose }) {
  return (
    <div className={styles.lightbox} onClick={onClose}>
      <button className={styles.lightboxClose} onClick={onClose}>✕</button>
      {isPdfUrl(url) ? (
        <iframe
          src={url}
          title="PDF Document"
          style={{ width: '85vw', height: '90vh', border: 'none', borderRadius: '8px', background: '#fff' }}
          onClick={(e) => e.stopPropagation()}
        />
      ) : (
        <img src={url} alt="Document" className={styles.lightboxImg} onClick={(e) => e.stopPropagation()} />
      )}
    </div>
  );
}

// ── Date-only document card (registration sticker, expiration sticker) ───────
// These documents are stored ONLY as dates in user_documents — no file upload.
// So we never want to show "Not uploaded" for them; we show the date with color coding.
function DateDocCard({ label, expiry }) {
  const today = new Date();
  const exp = expiry ? new Date(expiry) : null;
  const threeMonths = new Date(); threeMonths.setMonth(today.getMonth() + 3);
  const isExpired = exp && exp < today;
  const isSoon = exp && exp >= today && exp <= threeMonths;
  const color = isExpired ? '#dc2626' : isSoon ? '#d97706' : '#16a34a';
  const icon = isExpired ? '🔴' : isSoon ? '⚠️' : '✅';
  const bg = isExpired ? '#fef2f2' : isSoon ? '#fffbeb' : '#f0fdf4';
  const border = isExpired ? '#fecaca' : isSoon ? '#fde68a' : '#bbf7d0';

  return (
    <div className={styles.docCard} style={{ background: bg, border: `1.5px solid ${border}` }}>
      <div className={styles.docCardLabel}>{label}</div>
      <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        padding: '28px 16px', borderRadius: '8px', background: '#fff', minHeight: '110px',
        border: '1px dashed #d1d5db'
      }}>
        <div style={{ fontSize: '30px', marginBottom: '6px' }}>
          {isExpired ? '📅' : isSoon ? '⏰' : '📆'}
        </div>
        {exp ? (
          <div style={{ fontSize: '14px', fontWeight: 700, color }}>
            {icon} {fmt(expiry)}
          </div>
        ) : (
          <div style={{ fontSize: '13px', color: '#9ca3af' }}>Not set</div>
        )}
      </div>
    </div>
  );
}

// ── Document card ─────────────────────────────────────────────────────────────
function DocCard({ url, label, expiry }) {
  const [lightbox, setLightbox] = useState(false);
  if (!url) {
    // Only show "Not uploaded" for file-backed docs, NOT for date-only docs.
    // (Call sites pass registration/expiration sticker to DateDocCard instead.)
    return (
      <div className={styles.docCardMissing}>
        <span className={styles.docCardMissingIcon}>📎</span>
        <span>{label}</span>
        <span className={styles.docCardMissingNote}>Not uploaded</span>
      </div>
    );
  }

  const isImage = isImageUrl(url);
  const isPdf = !isImage && isPdfUrl(url);

  // Expiry color coding
  let expiryDisplay = null;
  if (expiry) {
    const today = new Date();
    const exp = new Date(expiry);
    const threeMonths = new Date();
    threeMonths.setMonth(today.getMonth() + 3);
    const color = exp < today ? '#dc2626' : exp <= threeMonths ? '#d97706' : '#16a34a';
    const icon = exp < today ? '🔴' : exp <= threeMonths ? '⚠️' : '✅';
    expiryDisplay = (
      <span style={{ fontSize: '11px', color, fontWeight: 600, marginTop: '4px', display: 'block' }}>
        {icon} Exp: {fmt(expiry)}
      </span>
    );
  }

  return (
    <div className={styles.docCard}>
      <div className={styles.docCardLabel}>{label}</div>
      {isImage ? (
        <div className={styles.docCardImgWrap} onClick={() => setLightbox(true)}>
          <img src={url} alt={label} className={styles.docCardImg} />
          <div className={styles.docCardImgOverlay}>🔍 Click to zoom</div>
        </div>
      ) : isPdf ? (
        <a href={url} target="_blank" rel="noreferrer" className={styles.docCardFile} onClick={(e) => { e.preventDefault(); setLightbox(true); }}>
          <span style={{ fontSize: 32 }}>📄</span>
          <div>
            <div className={styles.docCardFileName}>PDF Document</div>
            <div className={styles.docCardFileMeta}>Click to open ↗</div>
          </div>
        </a>
      ) : (
        <a href={url} target="_blank" rel="noreferrer" className={styles.docCardFile}>
          <span style={{ fontSize: 32 }}>📎</span>
          <div>
            <div className={styles.docCardFileName}>Document</div>
            <div className={styles.docCardFileMeta}>Click to open ↗</div>
          </div>
        </a>
      )}
      {expiryDisplay}
      {lightbox && <Lightbox url={url} onClose={() => setLightbox(false)} />}
    </div>
  );
}

const PAYOUT_STATUS_OPTIONS = ['not_started', 'pending', 'enabled', 'blocked'];

// ── Detail panel (right side) ─────────────────────────────────────────────────
function DetailPanel({
  app, onClose, onApprove, onReject, onRequestChanges, approving,
  onSuspend, onReinstate, suspending,
  onPayoutChange, payoutSaving,
}) {
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [showSuspendForm, setShowSuspendForm] = useState(false);
  const [suspendReason, setSuspendReason] = useState('');

  if (!app) return (
    <div className={styles.detailEmpty}>
      <span style={{ fontSize: 40 }}>📋</span>
      <p>Select an application to view details</p>
    </div>
  );

  return (
    <div className={styles.detailPanel}>
      <div className={styles.detailPanelHeader}>
        <h2 className={styles.detailPanelTitle}>Application Detail</h2>
        <button className={styles.detailPanelClose} onClick={onClose}>✕</button>
      </div>

      <div className={styles.detailPanelBody}>
        {/* Applicant */}
        <section className={styles.detailSection}>
          <h3 className={styles.detailSectionTitle}>Applicant</h3>
          <InfoRow label="Full legal name" value={app.users?.full_name} />
          <InfoRow label="Date of birth" value={app.users?.date_of_birth} />
          <InfoRow label="Residential address" value={app.users?.address} />
          <InfoRow label="Email" value={app.users?.email} />
          <InfoRow label="Phone" value={app.users?.phone} />
          <InfoRow label="Driver License" value={app.users?.driver_license_number} />
          <InfoRow label="Joined" value={fmt(app.users?.created_at)} />
        </section>

        {/* Application */}
        <section className={styles.detailSection}>
          <h3 className={styles.detailSectionTitle}>Application</h3>
          <InfoRow label="Submitted" value={fmt(app.created_at)} />
          <InfoRow label="Owner Type" value={<Badge value={app.owner_type || 'private_individual'} />} />
          <InfoRow label="Insurance Path" value={<Badge value={app.insurance_path} />} />
          <InfoRow label="Status" value={<Badge value={app.status} />} />
          <InfoRow label="Country" value={app.country_code || '—'} />
          <InfoRow label="State / Region" value={app.state_region || '—'} />
          {app.insurance_path === 'path_b' && (
            <>
              <InfoRow label="Prepayment" value={`$${app.prepayment_amount}`} />
              <InfoRow label="Coverage" value={app.coverage_type} />
            </>
          )}
          {app.admin_notes && <InfoRow label="Notes" value={app.admin_notes} />}
          {app.status === 'suspended' && app.suspended_reason && (
            <InfoRow label="Suspended because" value={app.suspended_reason} />
          )}
        </section>

        {/* Requirements from "Vehicle Lessor Requirements" for this country + type */}
        {Array.isArray(app.requirements) && (
          <section className={styles.detailSection}>
            <h3 className={styles.detailSectionTitle}>Requirements ({app.requirements.filter((r) => r.met).length} of {app.requirements.length} complete)</h3>
            {app.requirements.length === 0 && <p style={{ fontSize: 13, color: '#64748b', margin: 0 }}>No requirements configured for this country and account type.</p>}
            {app.requirements.map((r) => (
              <InfoRow key={r.key} label={r.label} value={
                <span style={{ fontWeight: 700, color: r.met ? '#166534' : '#991b1b' }}>
                  {r.met ? '✓ Complete' : '✗ Missing'}{r.document_status && r.document_status !== 'missing' ? ` · document ${r.document_status}` : ''}
                </span>
              } />
            ))}
          </section>
        )}

        {/* Company / Fleet details — only shown for company_fleet applications */}
        {app.owner_type === 'company_fleet' && (
          <section className={styles.detailSection}>
            <h3 className={styles.detailSectionTitle}>Company / Fleet Details</h3>
            <InfoRow label="Company name" value={app.company_name} />
            <InfoRow label="Trade name" value={app.trade_name} />
            <InfoRow label="Legal form" value={app.legal_form} />
            <InfoRow label="Business address" value={app.business_address} />
            <InfoRow label="Registration number" value={app.company_registration_number} />
            <InfoRow label="Tax ID" value={app.tax_id} />
            <InfoRow label="Representative" value={app.rep_name} />
            <InfoRow label="Rep. position" value={app.rep_position} />
            <InfoRow label="Rep. email" value={app.rep_email} />
            <InfoRow label="Rep. phone" value={app.rep_phone} />
            {app.rep_authorization_signed_url && <InfoRow label="Rep. authorization" value={<a href={app.rep_authorization_signed_url} target="_blank" rel="noreferrer">View file ↗</a>} />}
            {app.business_registration_signed_url && <InfoRow label="Business registration" value={<a href={app.business_registration_signed_url} target="_blank" rel="noreferrer">View file ↗</a>} />}
            {app.fleet_permits_signed_url && <InfoRow label="Fleet permits" value={<a href={app.fleet_permits_signed_url} target="_blank" rel="noreferrer">View file ↗</a>} />}
          </section>
        )}

        {/* Payouts — independent from role_status (doc Section 5) */}
        <section className={styles.detailSection}>
          <h3 className={styles.detailSectionTitle}>Payouts</h3>
          <InfoRow
            label="Payout status"
            value={<Badge value={app.payout_status || 'not_started'} />}
          />
          <div className={styles.payoutRow}>
            {PAYOUT_STATUS_OPTIONS.map((opt) => (
              <button
                key={opt}
                className={opt === (app.payout_status || 'not_started') ? styles.payoutBtnActive : styles.payoutBtn}
                disabled={payoutSaving || opt === (app.payout_status || 'not_started')}
                onClick={() => onPayoutChange(app, opt)}
              >
                {opt.replace('_', ' ')}
              </button>
            ))}
          </div>
        </section>

        {/* Documents */}
        <section className={styles.detailSection}>
          <h3 className={styles.detailSectionTitle}>Documents</h3>
          <div className={styles.docGrid}>
            <DocCard url={app.driver_license_signed_url} label="Driver's License" expiry={app.driver_license_expiry} />
            <DocCard url={app.id_document_signed_url} label="ID Document" expiry={app.id_document_expiry} />
            <DocCard url={app.selfie_signed_url} label="Live selfie" expiry={null} />
            <DocCard url={app.ownership_proof_signed_url} label="Proof of ownership / authorization" expiry={null} />
            <DocCard url={app.address_proof_signed_url} label="Address Proof" expiry={null} />
            <DocCard url={app.vehicle_registration_signed_url} label="Vehicle Registration" expiry={app.vehicle_registration_expiry} />
            <DocCard url={app.insurance_proof_signed_url} label="Insurance Proof" expiry={app.insurance_expiry} />
            <DateDocCard label="Registration Sticker Expiry" expiry={app.registration_sticker_expiry} />
            <DateDocCard label="Expiration Sticker Expiry" expiry={app.expiration_sticker_expiry} />
          </div>
        </section>

        {/* Actions — Point 30: show for submitted, changes_requested AND rejected (to allow re-review) */}
        {['submitted', 'changes_requested', 'rejected'].includes(app.status) && !showRejectForm && (
          <div className={styles.detailActions}>
            <button className={styles.approveBtn} onClick={() => onApprove(app)} disabled={approving}>
              ✓ Approve Application
            </button>
            {app.status === 'submitted' && (
              <button className={styles.suspendBtn} onClick={() => onRequestChanges(app)} disabled={approving}>
                ✏️ Request Changes
              </button>
            )}
            <button className={styles.rejectBtn} onClick={() => setShowRejectForm(true)}>
              ✕ Reject
            </button>
          </div>
        )}

        {/* Suspend / Reinstate — approved owners can be locked out without losing history */}
        {app.status === 'approved' && !showSuspendForm && (
          <div className={styles.detailActions}>
            <button className={styles.suspendBtn} onClick={() => setShowSuspendForm(true)} disabled={suspending}>
              🚫 Suspend Owner
            </button>
          </div>
        )}
        {app.status === 'suspended' && (
          <div className={styles.detailActions}>
            <button className={styles.approveBtn} onClick={() => onReinstate(app)} disabled={suspending}>
              ↩ Reinstate Owner
            </button>
          </div>
        )}

        {showSuspendForm && (
          <div className={styles.rejectForm}>
            <p className={styles.rejectFormLabel}>Suspension reason (required)</p>
            <textarea
              className={styles.rejectTextarea}
              rows={3}
              value={suspendReason}
              onChange={(e) => setSuspendReason(e.target.value)}
              placeholder="Enter reason…"
              autoFocus
            />
            <div className={styles.rejectFormActions}>
              <button className={styles.cancelBtn} onClick={() => { setShowSuspendForm(false); setSuspendReason(''); }}>Cancel</button>
              <button
                className={styles.rejectBtn}
                disabled={!suspendReason.trim()}
                onClick={() => { onSuspend(app, suspendReason); setShowSuspendForm(false); setSuspendReason(''); }}
              >
                Confirm Suspend
              </button>
            </div>
          </div>
        )}

        {showRejectForm && (
          <div className={styles.rejectForm}>
            <p className={styles.rejectFormLabel}>Rejection reason (required)</p>
            <textarea
              className={styles.rejectTextarea}
              rows={3}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Enter reason…"
              autoFocus
            />
            <div className={styles.rejectFormActions}>
              <button className={styles.cancelBtn} onClick={() => { setShowRejectForm(false); setRejectReason(''); }}>Cancel</button>
              <button
                className={styles.rejectBtn}
                disabled={!rejectReason.trim()}
                onClick={() => { onReject(app, rejectReason); setShowRejectForm(false); setRejectReason(''); }}
              >
                Confirm Reject
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div className={styles.infoRow}>
      <span className={styles.infoLabel}>{label}</span>
      <span className={styles.infoValue}>{value || '—'}</span>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function Applications() {
  const queryClient = useQueryClient();
  const { user: adminUser } = useAuth();
  const { status: urlStatus } = useParams();

  // Derive filter from URL — 'all' means no filter
  const statusFilter = urlStatus || 'all';
  const displayFilter = urlStatus || 'submitted';

  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(null);
  // The list row has no requirements checklist / extra document links: load
  // the full application when one is opened (Vehicle Lessor Requirements).
  const selectedId = selected?.id;
  useEffect(() => {
    if (!selectedId) return undefined;
    let cancelled = false;
    getApplication(selectedId)
      .then((res) => { if (!cancelled && res.data?.application) setSelected((cur) => (cur && cur.id === selectedId ? { ...cur, ...res.data.application } : cur)); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [selectedId]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [deleteAllModal, setDeleteAllModal] = useState(false);
  const [deleteAllNameInput, setDeleteAllNameInput] = useState('');

  // Reset page and selection when URL filter changes
  useEffect(() => {
    setPage(1);
    setSelected(null);
    setSelectedIds(new Set());
  }, [urlStatus]);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['applications', statusFilter, page],
    queryFn: () => getApplications({ status: statusFilter, page, limit: 20 }).then((r) => r.data),
    keepPreviousData: true,
  });

  const approveMutation = useMutation({
    mutationFn: ({ id, notes }) => approveApplication(id, notes),
    onSuccess: () => {
      toast.success('Application approved');
      queryClient.invalidateQueries({ queryKey: ['applications'] });
      setSelected(null);
    },
    onError: (err) => toast.error(err.response?.data?.error || 'Failed to approve'),
  });

  const rejectMutation = useMutation({
    mutationFn: ({ id, reason }) => rejectApplication(id, reason),
    onSuccess: () => {
      toast.success('Application rejected');
      queryClient.invalidateQueries({ queryKey: ['applications'] });
      setSelected(null);
    },
    onError: (err) => toast.error(err.response?.data?.error || 'Failed to reject'),
  });

  const requestChangesMutation = useMutation({
    mutationFn: ({ id, reason }) => requestChangesApplication(id, reason),
    onSuccess: () => {
      toast.success('Changes requested');
      queryClient.invalidateQueries({ queryKey: ['applications'] });
      setSelected(null);
    },
    onError: (err) => toast.error(err.response?.data?.error || 'Failed to request changes'),
  });

  const suspendMutation = useMutation({
    mutationFn: ({ id, reason }) => suspendApplication(id, reason),
    onSuccess: () => {
      toast.success('Vehicle owner suspended — listings taken offline');
      queryClient.invalidateQueries({ queryKey: ['applications'] });
      setSelected(null);
    },
    onError: (err) => toast.error(err.response?.data?.error || 'Failed to suspend'),
  });

  const reinstateMutation = useMutation({
    mutationFn: ({ id }) => reinstateApplication(id),
    onSuccess: () => {
      toast.success('Vehicle owner reinstated');
      queryClient.invalidateQueries({ queryKey: ['applications'] });
      setSelected(null);
    },
    onError: (err) => toast.error(err.response?.data?.error || 'Failed to reinstate'),
  });

  const payoutMutation = useMutation({
    mutationFn: ({ id, payout_status }) => updatePayoutStatus(id, payout_status),
    onSuccess: (_data, vars) => {
      toast.success(`Payout status set to "${vars.payout_status}"`);
      queryClient.invalidateQueries({ queryKey: ['applications'] });
      setSelected((cur) => (cur ? { ...cur, payout_status: vars.payout_status } : cur));
    },
    onError: (err) => toast.error(err.response?.data?.error || 'Failed to update payout status'),
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: (ids) => deleteApplications(ids),
    onSuccess: () => {
      toast.success('Applications deleted');
      setSelectedIds(new Set());
      queryClient.invalidateQueries({ queryKey: ['applications'] });
    },
    onError: (err) => toast.error(err.response?.data?.error || 'Delete failed'),
  });

  const deleteAllMutation = useMutation({
    mutationFn: (status) => deleteAllApplications(status),
    onSuccess: () => {
      toast.success('Applications deleted');
      setSelectedIds(new Set());
      queryClient.invalidateQueries({ queryKey: ['applications'] });
    },
    onError: (err) => toast.error(err.response?.data?.error || 'Delete failed'),
  });

  function handleApprove(app) {
    if (window.confirm(`Approve application from ${app.users?.full_name}?`)) {
      approveMutation.mutate({ id: app.id });
    }
  }

  function handleRequestChanges(app) {
    const reason = window.prompt('What needs to be corrected?');
    if (reason && reason.trim()) requestChangesMutation.mutate({ id: app.id, reason: reason.trim() });
  }

  function handleReject(app, reason) {
    rejectMutation.mutate({ id: app.id, reason });
  }

  function handleSuspend(app, reason) {
    suspendMutation.mutate({ id: app.id, reason });
  }

  function handleReinstate(app) {
    if (window.confirm(`Reinstate ${app.users?.full_name}? They will need to re-publish each vehicle.`)) {
      reinstateMutation.mutate({ id: app.id });
    }
  }

  function handlePayoutChange(app, payout_status) {
    if (window.confirm(`Set payout status to "${payout_status}" for ${app.users?.full_name}?`)) {
      payoutMutation.mutate({ id: app.id, payout_status });
    }
  }

  function toggleSelect(id) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  function toggleSelectAll() {
    const apps = data?.applications || [];
    if (selectedIds.size === apps.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(apps.map((a) => a.id)));
    }
  }

  function handleBulkDelete() {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Delete ${selectedIds.size} application(s)? This cannot be undone.`)) return;
    bulkDeleteMutation.mutate([...selectedIds]);
  }

  // Point 31: open name-confirmation modal instead of window.confirm
  function handleDeleteAll() {
    setDeleteAllNameInput('');
    setDeleteAllModal(true);
  }

  function confirmDeleteAll() {
    const expectedName = adminUser?.full_name || '';
    if (deleteAllNameInput.trim() !== expectedName.trim()) {
      toast.error('Name does not match. Deletion cancelled.');
      return;
    }
    setDeleteAllModal(false);
    deleteAllMutation.mutate(statusFilter === 'all' ? null : statusFilter);
  }

  const pagination = data?.pagination;
  const apps = data?.applications || [];
  const allSelected = apps.length > 0 && selectedIds.size === apps.length;

  return (
    <>
    <div className={styles.pageLayout}>
      {/* ── Left: table ── */}
      <div className={`${styles.tablePane} ${selected ? styles.tablePaneNarrow : ''}`}>
        <div className={styles.header}>
          <h1 className={styles.pageTitle}>
            Applications
            {urlStatus && urlStatus !== 'all' && (
              <span className={styles.filterBadge}>
                {urlStatus.charAt(0).toUpperCase() + urlStatus.slice(1)}
              </span>
            )}
          </h1>
          <div className={styles.headerRight}>
            {selectedIds.size > 0 && (
              <button className={styles.bulkDeleteBtn} onClick={handleBulkDelete} disabled={bulkDeleteMutation.isPending}>
                🗑 Delete {selectedIds.size} selected
              </button>
            )}
            <button className={styles.deleteAllBtn} onClick={handleDeleteAll} disabled={deleteAllMutation.isPending}>
              Delete All {urlStatus === 'all' ? 'applications' : displayFilter}
            </button>
          </div>
        </div>

        {isLoading && <p className={styles.msg}>Loading…</p>}
        {isError && <p className={styles.msgError}>Failed to load applications.</p>}

        {data && (
          <>
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>
                      <input type="checkbox" checked={allSelected} onChange={toggleSelectAll}
                        aria-label="Select all" />
                    </th>
                    <th>Applicant</th>
                    <th>Country</th>
                    <th>Submitted</th>
                    <th>Path</th>
                    <th>Status</th>
                    <th>Vehicle Reg</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {apps.length === 0 && (
                    <tr><td colSpan={8} className={styles.empty}>
                      {statusFilter === 'all' ? 'No applications.' : `No ${statusFilter} applications.`}
                    </td></tr>
                  )}
                  {apps.map((app) => (
                    <tr key={app.id}
                      className={`${selected?.id === app.id ? styles.rowSelected : ''}`}
                      onClick={() => setSelected(app)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td onClick={(e) => e.stopPropagation()}>
                        <input type="checkbox" checked={selectedIds.has(app.id)}
                          onChange={() => toggleSelect(app.id)} aria-label="Select row" />
                      </td>
                      <td>
                        <div className={styles.applicantCell}>
                          <span className={styles.applicantName}>{app.users?.full_name || '—'}</span>
                          <span className={styles.applicantEmail}>{app.users?.email}</span>
                        </div>
                      </td>
                      <td>{app.country_code || '—'}</td>
                      <td className={styles.dateCell}>{fmt(app.created_at)}</td>
                      <td><Badge value={app.insurance_path} /></td>
                      <td><Badge value={app.status} /></td>
                      <td>
                        <span className={app.vehicle_registration_signed_url ? styles.docChip : styles.docChipMissing}>
                          {app.vehicle_registration_signed_url ? '✓' : '✗'}
                        </span>
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        {(app.status === 'submitted' || app.status === 'changes_requested') && (
                          <div className={styles.actions}>
                            <button className={styles.approveBtn} onClick={() => handleApprove(app)} disabled={approveMutation.isPending}>✓</button>
                            <button className={styles.rejectBtn} onClick={() => {
                              const r = window.prompt('Rejection reason:');
                              if (r) handleReject(app, r);
                            }}>✕</button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination page={pagination?.page || 1} pages={pagination?.pages || 1} onPage={setPage} />
          </>
        )}
      </div>

      {/* ── Right: detail panel ── */}
      <DetailPanel
        app={selected}
        onClose={() => setSelected(null)}
        onApprove={handleApprove}
        onReject={handleReject}
        onRequestChanges={handleRequestChanges}
        approving={approveMutation.isPending || requestChangesMutation.isPending}
        onSuspend={handleSuspend}
        onReinstate={handleReinstate}
        suspending={suspendMutation.isPending || reinstateMutation.isPending}
        onPayoutChange={handlePayoutChange}
        payoutSaving={payoutMutation.isPending}
      />
    </div>

    {/* Point 31: Name-confirmation modal for Delete All */}
    {deleteAllModal && (
      <div style={modalOverlayStyle}>
        <div style={modalBoxStyle}>
          <h3 style={{ marginTop: 0, fontSize: 17, fontWeight: 700 }}>
            Confirm Delete All {urlStatus === 'all' ? 'applications' : urlStatus}
          </h3>
          <p style={{ fontSize: 14, color: '#374151', marginBottom: 16 }}>
            This will permanently delete <strong>all {urlStatus === 'all' ? 'applications' : `${urlStatus} applications`}</strong>. This cannot be undone.
          </p>
          <p style={{ fontSize: 14, color: '#374151', marginBottom: 8 }}>
            Type your full name to confirm: <strong>{adminUser?.full_name}</strong>
          </p>
          <input
            type="text"
            style={modalInputStyle}
            placeholder="Your full name"
            value={deleteAllNameInput}
            onChange={(e) => setDeleteAllNameInput(e.target.value)}
            autoFocus
          />
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 16 }}>
            <button style={modalCancelBtnStyle} onClick={() => setDeleteAllModal(false)}>
              Cancel
            </button>
            <button
              style={modalConfirmBtnStyle}
              onClick={confirmDeleteAll}
              disabled={deleteAllMutation.isPending}
            >
              {deleteAllMutation.isPending ? 'Deleting…' : 'Delete All'}
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
}

const modalOverlayStyle = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
};
const modalBoxStyle = {
  background: '#fff', borderRadius: 12, padding: '28px 28px 24px',
  width: '100%', maxWidth: 440, boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
};
const modalInputStyle = {
  width: '100%', boxSizing: 'border-box', padding: '10px 12px',
  border: '1.5px solid #d1d5db', borderRadius: 8, fontSize: 14,
};
const modalCancelBtnStyle = {
  padding: '8px 18px', background: '#f3f4f6', border: '1px solid #e5e7eb',
  borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 500,
};
const modalConfirmBtnStyle = {
  padding: '8px 18px', background: '#dc2626', border: 'none', color: '#fff',
  borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600,
};
